import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Download, CheckCircle2, XCircle, Pencil, Printer, ArrowUpRight, Package, History } from "lucide-react";
import CopyDeepLinkButton from "../safety/CopyDeepLinkButton";
import SafetyShell from "../safety/SafetyShell";
import { pushRecent } from "../safety/recents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_BADGES = {
  draft: { label: "Draft", className: "bg-gray-100 text-gray-700" },
  pending_approval: { label: "Pending Approval", className: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", className: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700" },
};

export default function ProductionReportView({ user, onLogout }) {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reportId]);

  const load = async () => {
    try {
      const [permRes, res] = await Promise.all([
        axios.get(`${API}/auth/me/permissions`, auth()),
        axios.get(`${API}/production/reports/${reportId}`, auth()),
      ]);
      setPermissions(permRes.data.effective_permissions || []);
      setReport(res.data);
    } catch (e) {
      toast.error("Failed to load report");
    } finally { setLoading(false); }
  };

  const canApprove = permissions.includes("production.approve");

  const approve = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/production/reports/${reportId}/approve`, {}, auth());
      toast.success("Approved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setWorking(false); }
  };

  const reject = async () => {
    if (!rejectRemark.trim()) { toast.error("Rejection remark required"); return; }
    setWorking(true);
    try {
      await axios.post(`${API}/production/reports/${reportId}/reject`, { remark: rejectRemark.trim() }, auth());
      toast.success("Rejected"); setRejectDialog(false); setRejectRemark(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setWorking(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/production/reports/${reportId}/pdf`, { ...auth(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `DailyProductionReport_${report.report_date}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const printPdf = async () => {
    try {
      const res = await axios.get(`${API}/production/reports/${reportId}/pdf`, { ...auth(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Remove any existing print iframe
      const existing = document.getElementById("production-report-print-frame");
      if (existing) existing.remove();
      const iframe = document.createElement("iframe");
      iframe.id = "production-report-print-frame";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          toast.error("Print blocked by browser. Please allow pop-ups / printing.");
        }
      };
      document.body.appendChild(iframe);
      // Cleanup object URL after a delay so print dialog has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Failed to print PDF"); }
  };

  if (loading || !report) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
    </div>;
  }
  const badge = STATUS_BADGES[report.status] || STATUS_BADGES.draft;
  const permSet = new Set(permissions);
  const canEditPermission = permSet.has("production.create") || permSet.has("production.approve");
  const isCreator = report.created_by === user?.email;
  const canEdit =
    (report.status === "draft" && isCreator && permSet.has("production.create")) ||
    (report.status === "pending_approval" && canEditPermission && (isCreator || permSet.has("production.approve")));

  // Build unique linked POs from report items
  const linkedPos = (() => {
    if (!report) return [];
    const seen = new Map();
    (report.items || []).forEach((it) => {
      if (it.po_id && !seen.has(it.po_id)) {
        seen.set(it.po_id, { po_id: it.po_id, po_number: it.po_number });
      }
    });
    return Array.from(seen.values());
  })();

  const goPo = (poId, poNumber, productCode) => {
    pushRecent({
      type: "po", id: poId, label: `PO ${poNumber}`,
      sublabel: productCode ? `From Report ${report.report_date} · ${productCode}` : `From Report ${report.report_date}`,
      path: `/safety/po/${poId}/detail${productCode ? `?line=${encodeURIComponent(productCode)}` : ""}`,
    });
    navigate(`/safety/po/${poId}/detail${productCode ? `?line=${encodeURIComponent(productCode)}` : ""}`);
  };

  const rightRail = (
    <div className="space-y-4">
      <div className="sf-right-rail" data-testid="report-rail-status">
        <h4>Status</h4>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between"><span className="text-zinc-500">Status</span><span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${badge.className}`}>{badge.label}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Date</span><span className="sf-mono">{report?.report_date}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Working Hrs</span><span className="sf-mono">{report?.working_hours}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Manpower</span><span className="sf-mono">{report?.manpower_count}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Created</span><span className="truncate ml-2 text-right">{report?.created_by_name || "—"}</span></div>
          {report?.approved_by_name && (
            <div className="flex justify-between"><span className="text-zinc-500">Approved</span><span className="truncate ml-2 text-right">{report.approved_by_name}</span></div>
          )}
        </div>
      </div>

      <div className="sf-right-rail" data-testid="report-rail-pos">
        <div className="flex items-center justify-between mb-1">
          <h4 className="flex items-center gap-1.5"><Package className="w-3 h-3" /> Linked POs</h4>
          {linkedPos.length > 0 && <span className="text-[10px] text-zinc-400">{linkedPos.length}</span>}
        </div>
        {linkedPos.length === 0 ? (
          <p className="text-xs text-zinc-400 py-2">No linked POs.</p>
        ) : (
          <ul className="space-y-1 mt-2">
            {linkedPos.map((p) => (
              <li key={p.po_id}>
                <button
                  onClick={() => goPo(p.po_id, p.po_number)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 text-left"
                  data-testid={`rail-linked-po-${p.po_number}`}
                >
                  <ArrowUpRight className="w-3 h-3 text-zinc-400" />
                  <span className="text-xs sf-mono">{p.po_number}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[
        { label: "Safety", to: "/safety" },
        { label: "Production Reports", to: "/safety/production-reports" },
        { label: report?.report_date || "…" },
      ]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
        { label: report?.report_date || "Report", active: true, depth: 2 },
      ]}
      rightRail={rightRail}
    >
      <div data-testid="production-report-view">
      {/* Header bar */}
      <div className="sf-surface p-4 mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Daily Production Report</h1>
          <div className="text-sm text-zinc-500 mt-0.5 sf-mono">{report.report_date}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
            {canEdit && (
              <Button onClick={() => navigate(`/safety/production-reports/${reportId}/edit`)} variant="outline" data-testid="edit-btn">
                <Pencil className="w-4 h-4 mr-1" /> Edit
              </Button>
            )}
            <Button onClick={downloadPdf} variant="outline" data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button onClick={printPdf} variant="outline" data-testid="print-pdf-btn">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <CopyDeepLinkButton variant="outline" testId="report-detail-copy-link" />
            {canApprove && report.status === "pending_approval" && (
              <>
                <Button onClick={approve} disabled={working} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                </Button>
                <Button onClick={() => setRejectDialog(true)} variant="outline" className="text-red-600" data-testid="reject-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Status row */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className={`px-3 py-1 text-sm rounded ${badge.className}`} data-testid="report-status-badge">{badge.label}</span>
          {report.edited_after_submit && (
            <span
              className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700"
              title={`Edited after submission${report.last_edited_by_name ? ` by ${report.last_edited_by_name}` : ""}${report.last_edited_at ? ` on ${String(report.last_edited_at).slice(0, 19).replace("T", " ")}` : ""}`}
              data-testid="edited-after-submit-badge"
            >
              edited after submission
            </span>
          )}
        </div>

        <div className="space-y-4">
        {report.rejection_remark && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700" data-testid="rejection-remark">
            <b>Rejection remark:</b> {report.rejection_remark}
          </div>
        )}

        {/* Edit history (post-submission edits) */}
        {(report.edit_history || []).length > 0 && (
          <EditHistoryPanel history={report.edit_history} currentVersion={report.version} />
        )}

        {/* Header info */}
        <section className="bg-white rounded-lg border p-5 shadow-sm">
          <h2 className="font-semibold mb-3">Header</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Date" value={report.report_date} />
            <Field label="Plant Started" value={report.plant_started_at?.replace("T", " ")} />
            <Field label="Plant Stopped" value={report.plant_stopped_at?.replace("T", " ")} />
            <Field label="Working Hours" value={report.working_hours} />
            <Field label="Manpower" value={report.manpower_count} />
            <Field label="Gas Consumption (Kg)" value={report.gas_consumption_pret_kg} />
            <Field label="SO Nos" value={(report.po_numbers || []).join(", ")} />
            <Field label="Customer(s)" value={(report.customer_names || []).join(", ")} />
          </div>
        </section>

        {/* Powder */}
        <section className="bg-white rounded-lg border p-5 shadow-sm">
          <h2 className="font-semibold mb-3">Powder</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Batch No" value={report.batch_no} />
            <Field label="Shade" value={report.shade_name} />
            <Field label="Make" value={report.make_name} />
            <Field label="Withdrawn (kg)" value={report.withdrawn_kg} />
          </div>
          {(report.po_consumptions || []).length > 0 && (
            <div className="mt-3">
              <h3 className="text-sm font-medium mb-1">Per-PO Consumption</h3>
              <table className="w-full text-sm border">
                <thead className="bg-gray-50"><tr><th className="border p-2 text-left">PO No</th><th className="border p-2 text-right">kg</th></tr></thead>
                <tbody>
                  {(report.po_consumptions || []).map(c => (
                    <tr key={c.po_id || `unassigned-${c.po_number}`}>
                      <td className="border p-2">
                        {c.po_id ? (
                          <button onClick={() => goPo(c.po_id, c.po_number)} className="sf-link-lateral inline-flex items-center gap-1" data-testid={`report-pwd-po-${c.po_number}`}>
                            <ArrowUpRight className="w-3 h-3" />{c.po_number}
                          </button>
                        ) : c.po_number}
                      </td>
                      <td className="border p-2 text-right">{c.consumption_kg}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Items */}
        <section className="bg-white rounded-lg border p-5 shadow-sm">
          <h2 className="font-semibold mb-3">Production Status</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border p-2" colSpan="4">Order Description</th>
                  <th className="border p-2" colSpan="3">Production Status</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border p-2 text-left">Product Code</th>
                  <th className="border p-2 text-left">Description</th>
                  <th className="border p-2 text-left">PO No.</th>
                  <th className="border p-2 text-right">Order Qty</th>
                  <th className="border p-2 text-right">Coated Qty</th>
                  <th className="border p-2 text-right">Rework Qty</th>
                  <th className="border p-2 text-right">Accepted OK Qty</th>
                </tr>
              </thead>
              <tbody>
                {(report.items || []).map((it, i) => (
                  <tr key={i} data-testid={`report-line-${i}`}>
                    <td className="border p-2">{it.product_code}</td>
                    <td className="border p-2">{it.description}</td>
                    <td className="border p-2">
                      {it.po_id ? (
                        <button
                          onClick={() => goPo(it.po_id, it.po_number, it.product_code)}
                          className="sf-link-lateral inline-flex items-center gap-1"
                          data-testid={`report-line-po-${i}`}
                          title={`Open PO ${it.po_number} · line ${it.product_code}`}
                        >
                          <ArrowUpRight className="w-3 h-3" />{it.po_number}
                        </button>
                      ) : it.po_number}
                    </td>
                    <td className="border p-2 text-right">{it.order_qty}</td>
                    <td className="border p-2 text-right">{it.coated_qty}</td>
                    <td className="border p-2 text-right">{it.rework_qty}</td>
                    <td className="border p-2 text-right font-semibold">{it.accepted_qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Footer */}
        <section className="bg-white rounded-lg border p-5 shadow-sm">
          <h2 className="font-semibold mb-3">Totals</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Field label="Total Powder Coated On Date (Sq Ft)" value={report.total_powder_coated_on_date} />
            <Field label="Total Sq Ft" value={report.total_sq_ft} />
            <Field label="Rework Sq Ft" value={report.rework_sq_ft} />
          </div>
          {report.notes && <div className="mt-3"><Field label="Notes" value={report.notes} /></div>}
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <Field label="Sign Dpt Incharge" value={report.created_by_name} />
            <Field label="Sign DH" value={report.approved_by_name || "—"} />
          </div>
        </section>
        </div>

      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject Report</DialogTitle></DialogHeader>
          <Textarea rows="4" placeholder="Reason for rejection" value={rejectRemark} onChange={(e) => setRejectRemark(e.target.value)} data-testid="reject-remark-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button onClick={reject} disabled={working} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SafetyShell>
  );
}

function EditHistoryPanel({ history, currentVersion }) {
  const [open, setOpen] = useState(true);
  const sorted = [...(history || [])].sort((a, b) => (b.edited_at || "").localeCompare(a.edited_at || ""));
  const fmt = (v) => {
    if (v === null || v === undefined) return <span className="text-zinc-400 italic">—</span>;
    if (v === "") return <span className="text-zinc-400 italic">empty</span>;
    return <span className="sf-mono">{String(v)}</span>;
  };
  return (
    <section className="bg-white border rounded-lg shadow-sm overflow-hidden" data-testid="edit-history-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 bg-blue-50/40 border-b hover:bg-blue-50"
        data-testid="edit-history-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-blue-700" />
          <h2 className="font-semibold text-blue-900">Edit History</h2>
          <span className="text-xs text-blue-700 px-2 py-0.5 bg-blue-100 rounded">
            {sorted.length} change{sorted.length === 1 ? "" : "s"}
          </span>
          {currentVersion ? (
            <span className="text-xs text-zinc-500 sf-mono">· current v{currentVersion}</span>
          ) : null}
        </div>
        <span className="text-xs text-blue-700">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="divide-y">
          {sorted.map((h) => (
            <div key={h.id} className="p-4" data-testid={`edit-history-entry-${h.version_after}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="text-sm">
                  <span className="font-semibold sf-mono">v{h.version_after}</span>
                  <span className="text-zinc-500"> · {String(h.edited_at || "").slice(0, 19).replace("T", " ")} · </span>
                  <span className="font-medium">{h.edited_by_name || h.edited_by || "—"}</span>
                </div>
                <div className="text-xs text-zinc-500">
                  {(h.field_changes || []).length} field{(h.field_changes || []).length === 1 ? "" : "s"} changed
                </div>
              </div>
              {h.reason && (
                <div className="text-xs italic text-zinc-600 mb-2 pl-3 border-l-2 border-blue-200">
                  &ldquo;{h.reason}&rdquo;
                </div>
              )}
              <ul className="space-y-1 text-xs">
                {(h.field_changes || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-2 flex-wrap">
                    <span className="text-zinc-700 min-w-[180px]">{c.label}</span>
                    <span className="line-through text-red-600">{fmt(c.from)}</span>
                    <span className="text-zinc-400">→</span>
                    <span className="text-emerald-700 font-medium">{fmt(c.to)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium break-all">{value == null || value === "" ? "-" : String(value)}</div>
    </div>
  );
}
